import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { flashMessage } from '~/app/actions';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar, Spacer } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import Segmented from '~/app/ui/Segmented';
import EmptyState from '~/app/ui/EmptyState';
import Dialog from '~/app/ui/Dialog';
import { toggleCategoryNode } from '../actions';
import { onlyInOneRegion } from './regionCoverage';

const CATEGORIES = gql`
  query getCategories {
    sitemap {
      rootPages
      rootCategories
    }
    categories {
      _id
      path
      title
      updated
    }
    pagesActivePaths
  }
`;
const ADD_CATEGORY = gql`
  mutation addCategory($title: String!, $path: String!) {
    addCategory(title: $title, path: $path) {
      _id
    }
  }
`;

type TreePage = { _id: string; title: string; path: string };
type TreeCategory = { _id: string; title?: string; path: string; canUserEdit?: boolean; children?: TreeCategory[]; pages?: TreePage[] };
type Adding = { parent: TreeCategory | null } | null;

const SEGMENT = /^[A-Za-z0-9_-]+$/;

const useStyles = createUseStyles({
  tree: { background: A.surface, border: `1px solid ${S.line}`, borderRadius: 12, padding: '8px 0' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 38,
    paddingRight: 12,
    fontSize: 14,
    '&:hover': { background: A.rowHover },
    '&:hover $add': { opacity: 1 }
  },
  toggle: {
    all: 'unset',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 6,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 20, transition: 'transform 120ms ease' },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus,
    '&[aria-expanded="false"] .material-icons': { transform: 'rotate(-90deg)' }
  },
  spacer: { width: 24, flexShrink: 0 },
  icon: { fontSize: '19px !important', color: '#8E949C', [DARK]: { color: S.muted } },
  slug: { fontFamily: S.mono, fontSize: 13, fontWeight: 500 },
  title: { color: S.muted, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  count: { marginLeft: 'auto', fontSize: 12, color: S.muted, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  add: {
    all: 'unset',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 8px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    color: S.muted,
    cursor: 'pointer',
    opacity: 0,
    '& .material-icons': { fontSize: 16 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': { ...A.focus, opacity: 1 },
    '@media (hover: none)': { opacity: 1 }
  },
  page: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 32,
    paddingRight: 12,
    fontSize: 14,
    '&:hover': { background: A.rowHover }
  },
  pageLink: { color: S.fg, textDecoration: 'none', '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 }, '&:focus-visible': A.focus },
  pagePath: { fontFamily: S.mono, fontSize: 12, color: S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  only: {
    padding: '1px 6px',
    borderRadius: 4,
    background: TONES.amber.bg,
    color: TONES.amber.fg,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap'
  },
  label: { display: 'block', marginBottom: 6, fontSize: 13, fontWeight: 600 },
  field: {
    boxSizing: 'border-box',
    width: '100%',
    height: 38,
    padding: '0 12px',
    marginBottom: 14,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    fontFamily: S.ui,
    fontSize: 15,
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` }
  },
  preview: { margin: '-6px 0 0', fontSize: 13, color: S.muted, '& code': { fontFamily: S.mono, color: S.fg } },
  error: { margin: '-6px 0 0', fontSize: 13, color: S.red },
  summary: { marginBottom: 12, fontSize: 13, color: S.muted }
});

const aixc = (s: string) => s.replace(/cxc10/gi, 'aixc');

/** Pages under a category, at any depth. */
const pageCount = (cat: TreeCategory): number => (cat.pages || []).length + (cat.children || []).reduce((n, ch) => n + pageCount(ch), 0);

const CategoriesScreen: React.FC = () => {
  const c = useStyles();
  const dispatch = useDispatch();
  const expanded = useSelector((state: { categories: { expandedNodesById: Record<string, boolean> } }) => state.categories.expandedNodesById);
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const { data, loading, error, refetch } = useQuery(CATEGORIES, { fetchPolicy: 'cache-and-network' });
  const [addCategory] = useMutation(ADD_CATEGORY, { refetchQueries: ['getCategories'], awaitRefetchQueries: true });
  const [q, setQ] = useState('');
  const [view, setView] = useState<'categories' | 'pages' | 'one-region'>('pages');
  const [adding, setAdding] = useState<Adding>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newSegment, setNewSegment] = useState('');
  const [saving, setSaving] = useState(false);

  const roots: TreeCategory[] = useMemo(() => data?.sitemap?.rootCategories || [], [data]);
  const oneRegion = useMemo(() => onlyInOneRegion(data?.pagesActivePaths || []), [data]);
  const needle = q.trim().toLowerCase();

  // While searching or listing single-region pages, show only the branches with a match, opened.
  // The tree shows /cxc10 as "aixc", so a search for either finds it.
  const has = (text: string) => text.toLowerCase().includes(needle) || aixc(text).toLowerCase().includes(needle);
  const pageMatches = (p: TreePage) => {
    if (view === 'one-region' && !oneRegion.has(p.path)) return false;
    return !needle || has(p.title) || has(p.path);
  };
  const filtering = !!needle || view === 'one-region';
  const visible = (cat: TreeCategory): boolean => {
    if (!filtering) return true;
    const self = view !== 'one-region' && (has(cat.path) || has(String(cat.title || '')));
    return self || (cat.pages || []).some(pageMatches) || (cat.children || []).some(visible);
  };
  const isOpen = (cat: TreeCategory) => filtering || !!expanded[cat._id];

  const renderCategory = (cat: TreeCategory, depth: number): React.ReactNode => {
    if (!visible(cat)) return null;
    const open = isOpen(cat);
    const slug = aixc(cat.path.split('/').pop() || cat.path);
    const children = (cat.children || []);
    const pages = view === 'categories' ? [] : (cat.pages || []).filter(p => !filtering || pageMatches(p));
    const hasContent = children.length > 0 || (view !== 'categories' && (cat.pages || []).length > 0);
    const total = pageCount(cat);
    return (
      <div key={cat._id} role="treeitem" aria-expanded={hasContent ? open : undefined} aria-label={slug} data-category={cat.path}>
        <div className={c.row} style={{ paddingLeft: 12 + depth * 22 }}>
          {hasContent ? (
            <button type="button" className={c.toggle} aria-expanded={open} aria-label={open ? `Close ${slug}` : `Open ${slug}`} onClick={() => dispatch(toggleCategoryNode(cat._id))} disabled={filtering}>
              <i className="material-icons" aria-hidden>expand_more</i>
            </button>
          ) : <span className={c.spacer} />}
          <i className={`material-icons ${c.icon}`} aria-hidden>{open && hasContent ? 'folder_open' : 'folder'}</i>
          <span className={c.slug}>{slug}</span>
          {cat.title && cat.title !== slug && <span className={c.title}>{aixc(cat.title)}</span>}
          {cat.canUserEdit && (
            <button type="button" className={c.add} onClick={() => { setNewTitle(''); setNewSegment(''); setAdding({ parent: cat }); }} data-testid={`add-in-${cat.path}`}>
              <i className="material-icons" aria-hidden>add</i>Subcategory
            </button>
          )}
          <span className={c.count}>{total === 1 ? '1 page' : `${total} pages`}</span>
        </div>
        {open && (
          <div role="group">
            {children.map(ch => renderCategory(ch, depth + 1))}
            {pages.map(p => (
              <div key={p._id} className={c.page} style={{ paddingLeft: 12 + (depth + 1) * 22 + 24 }} data-tree-page={p.path}>
                <i className={`material-icons ${c.icon}`} style={{ fontSize: 17 }} aria-hidden>description</i>
                {/* People who can't edit this category get the page's versions (which link to the editor when allowed). */}
                <Link className={c.pageLink} to={cat.canUserEdit ? `/admin/page/${p._id}` : `/admin/page/versions/${p._id}`}>{p.title}</Link>
                <span className={c.pagePath}>{p.path}</span>
                {oneRegion.has(p.path) && view === 'one-region' && <span className={c.only}>{oneRegion.get(p.path)} only</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const parentPath = adding && adding.parent ? adding.parent.path : '';
  const segment = newSegment.trim();
  const segmentOk = SEGMENT.test(segment);
  const save = async () => {
    if (!segmentOk || !newTitle.trim() || saving) return;
    setSaving(true);
    try {
      await addCategory({ variables: { title: newTitle.trim(), path: `${parentPath}/${segment}` } });
      dispatch(flashMessage(`Added the category ${aixc(`${parentPath}/${segment}`)}.`));
      // Open the parent so the new category shows.
      if (adding && adding.parent && !expanded[adding.parent._id]) dispatch(toggleCategoryNode(adding.parent._id));
      setAdding(null);
    } catch (e) {
      dispatch(flashMessage((e as Error).message, 'ERROR'));
    } finally {
      setSaving(false);
    }
  };

  let body: React.ReactNode;
  if (error && !data) body = <EmptyState tone="error" title="Couldn’t load categories" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  else if (loading && !data) body = <EmptyState icon="hourglass_empty" title="Loading…" compact />;
  else if (roots.length === 0) body = <EmptyState icon="account_tree" title="No categories yet" />;
  else {
    const shown = roots.filter(visible);
    body = shown.length === 0
      ? <EmptyState icon="search_off" title={view === 'one-region' && !needle ? 'Every page has its counterpart' : 'Nothing matches'} compact>{view === 'one-region' && !needle ? 'Each US page has a CN page with the same address, and the other way round.' : undefined}</EmptyState>
      : <div className={c.tree} role="tree" aria-label="Categories" data-testid="category-tree">{shown.map(r => renderCategory(r, 0))}</div>;
  }

  return (
    <Page testId="categories-screen">
      <PageHeader
        title={tr('Categories', '页面分类')}
        description="Pages are grouped by category. A category sets the start of its pages’ addresses and who can edit and approve them."
        actions={isSuperAdmin ? <Button icon="create_new_folder" onClick={() => { setNewTitle(''); setNewSegment(''); setAdding({ parent: null }); }} data-testid="add-root-category">New top-level category</Button> : undefined}
      />
      <Toolbar>
        <Segmented<'categories' | 'pages' | 'one-region'>
          label="Show"
          value={view}
          onChange={setView}
          options={[
            { value: 'categories', label: 'Categories' },
            { value: 'pages', label: 'With pages' },
            { value: 'one-region', label: 'In one region only', count: data ? oneRegion.size : null, testId: 'view-one-region' }
          ]}
        />
        <SearchField value={q} onChange={setQ} placeholder="Search categories and pages" testId="categories-search" />
        <Spacer />
      </Toolbar>
      {view === 'one-region' && (
        <p className={c.summary}>Pages under /us or /cn whose address has no counterpart in the other region (for example /us/about without /cn/about).</p>
      )}
      {body}
      {adding && (
        <Dialog
          title={adding.parent ? `New category in ${aixc(adding.parent.path)}` : 'New top-level category'}
          onClose={() => setAdding(null)}
          width={460}
          testId="category-dialog"
          footer={(
            <>
              <Button onClick={() => setAdding(null)} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={save} disabled={!segmentOk || !newTitle.trim()} busy={saving} data-testid="category-save">Add category</Button>
            </>
          )}
        >
          <label className={c.label} htmlFor="category-title">Title</label>
          <input id="category-title" className={c.field} value={newTitle} autoFocus onChange={e => setNewTitle(e.target.value)} placeholder="What the pages in it are about" />
          <label className={c.label} htmlFor="category-segment">Address</label>
          <input id="category-segment" className={c.field} value={newSegment} onChange={e => setNewSegment(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder="e.g. events" />
          {segment && !segmentOk
            ? <p className={c.error} role="alert">Use letters, numbers, - and _ only, without slashes.</p>
            : <p className={c.preview}>Pages in it start with <code>{aixc(`${parentPath}/${segment || '…'}`)}</code></p>}
        </Dialog>
      )}
    </Page>
  );
};

export default CategoriesScreen;
